import { useEffect, useMemo, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { Minus, Sparkles } from "lucide-react";
import { resolveSeason, splitCountdown, formatSeasonDate, SEASON_EFFECTS } from "@/lib/seasons";
import SeasonEasterEgg from "@/components/SeasonEasterEgg";

// Habillage du site pendant un événement (Panel admin > Site > Événements) : liseré aux
// couleurs de la fête, carte avec compte à rebours, effets animés, surprise cachée, et des
// feux d'artifice sur l'accueil quand le compte à rebours arrive à zéro. Le panel peut
// afficher un aperçu sans enregistrer (événement `vp-season-preview`).

const FX_OFF_KEY = "vp_season_fx_off";
const FOLDED_KEY = "vp_season_folded";
const store = {
  get: (k) => { try { return localStorage.getItem(k); } catch { return null; } },
  set: (k, v) => { try { if (v == null) localStorage.removeItem(k); else localStorage.setItem(k, v); } catch { /* ignore */ } },
};
const reducedMotion = () => {
  try { return window.matchMedia("(prefers-reduced-motion: reduce)").matches; } catch { return false; }
};

export function hexToRgba(hex, alpha) {
  const m = /^#?([0-9a-f]{6})$/i.exec(hex || "");
  if (!m) return `rgba(99,102,241,${alpha})`;
  const n = parseInt(m[1], 16);
  return `rgba(${(n >> 16) & 255},${(n >> 8) & 255},${n & 255},${alpha})`;
}

const pad2 = (n) => String(n).padStart(2, "0");

export function SeasonCard({ season, cfg, now, onFold, fxOn, onToggleFx, inline = false }) {
  const [a, b] = season.colors || ["#6366f1", "#a855f7"];
  const left = season.target ? splitCountdown(season.target - now) : null;
  const showCountdown = cfg.countdown !== false && left;
  const tzNote = cfg.timezone && cfg.timezone !== "visitor" ? " (heure de France)" : "";
  return (
    <div
      className="rounded-2xl p-[1.5px] shadow-2xl"
      style={{ background: `linear-gradient(135deg, ${a}, ${b})`, width: inline ? "100%" : "min(330px, calc(100vw - 96px))" }}
      data-testid="season-card"
    >
      <div className="rounded-[15px] bg-[#0b0c12]/95 backdrop-blur-xl px-4 py-3.5 text-white">
        <div className="flex items-start gap-3">
          <div className="text-3xl leading-none select-none" aria-hidden="true">{season.emoji}</div>
          <div className="min-w-0 flex-1">
            <p className="font-semibold text-[15px] leading-tight truncate">{season.title}</p>
            <p className="text-white/50 text-xs mt-0.5">
              {season.phase === "countdown" ? season.label.charAt(0).toUpperCase() + season.label.slice(1) : season.target ? "En ce moment · se termine dans" : "C'est aujourd'hui !"}
            </p>
          </div>
          {!inline && (
            <div className="flex items-center gap-1 -mr-1 -mt-1">
              {onToggleFx && SEASON_EFFECTS[season.effect]?.kind && cfg.effects !== false && (
                <button type="button" onClick={onToggleFx} title={fxOn ? "Couper les effets" : "Remettre les effets"} className={`w-7 h-7 rounded-lg flex items-center justify-center hover:bg-white/10 ${fxOn ? "text-white/70" : "text-white/30"}`} aria-label="Effets animés">
                  <Sparkles className="w-3.5 h-3.5" />
                </button>
              )}
              {onFold && (
                <button type="button" onClick={onFold} title="Réduire" className="w-7 h-7 rounded-lg flex items-center justify-center text-white/60 hover:bg-white/10" aria-label="Réduire">
                  <Minus className="w-4 h-4" />
                </button>
              )}
            </div>
          )}
        </div>
        {showCountdown && (
          <div className="grid grid-cols-4 gap-1.5 mt-3" role="timer" aria-live="off">
            {[[left.days, "jours"], [left.hours, "heures"], [left.minutes, "min"], [left.seconds, "s"]].map(([value, unit]) => (
              <div key={unit} className="rounded-xl py-1.5 text-center" style={{ background: hexToRgba(a, 0.14), border: `1px solid ${hexToRgba(a, 0.3)}` }}>
                <div className="font-mono text-lg font-semibold tabular-nums leading-tight">{unit === "jours" ? value : pad2(value)}</div>
                <div className="text-[10px] uppercase tracking-wider text-white/50">{unit}</div>
              </div>
            ))}
          </div>
        )}
        {season.message && <p className="text-sm text-white/85 mt-3 leading-snug">{season.message}</p>}
        {showCountdown && (
          <p className="text-[11px] text-white/35 mt-2 first-letter:uppercase">{formatSeasonDate(season.target, cfg.timezone)}{tzNote}</p>
        )}
      </div>
    </div>
  );
}

// ---------- effets animés (canvas) ----------

function emojiSprite(char, size) {
  const c = document.createElement("canvas");
  const s = Math.ceil(size * 1.4);
  c.width = c.height = s;
  const g = c.getContext("2d");
  g.font = `${size}px "Apple Color Emoji","Segoe UI Emoji","Noto Color Emoji",sans-serif`;
  g.textAlign = "center";
  g.textBaseline = "middle";
  g.fillText(char, s / 2, s / 2 + size * 0.05);
  return c;
}

function SeasonEffects({ effect, colors, intensity = 1 }) {
  const ref = useRef(null);
  const colorsKey = (colors || []).join(",");
  useEffect(() => {
    const colors = colorsKey.split(",");
    const def = SEASON_EFFECTS[effect];
    const canvas = ref.current;
    if (!def || !def.kind || !canvas) return undefined;
    const ctx = canvas.getContext("2d");
    const dpr = Math.min(2, window.devicePixelRatio || 1);
    let w = 0, h = 0, raf = 0, last = performance.now(), nextRocket = 0;
    const resize = () => {
      w = window.innerWidth; h = window.innerHeight;
      canvas.width = w * dpr; canvas.height = h * dpr;
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    };
    resize();
    window.addEventListener("resize", resize);
    const palette = def.colors || [colors?.[0] || "#facc15", colors?.[1] || "#a855f7", "#ffffff", "#f472b6", "#38bdf8"];
    const area = (w * h) / (1280 * 800);
    const base = def.kind === "snow" ? 70 : def.kind === "confetti" ? 45 : 18;
    const count = Math.round(Math.max(8, Math.min(base * 1.6, base * area)) * intensity);
    const sprites = def.kind === "emoji" ? def.set.map((ch) => [18, 24, 30].map((size) => emojiSprite(ch, size))) : null;
    const rand = (a, b) => a + Math.random() * (b - a);
    const spawn = (p, initial) => {
      p.x = rand(0, w);
      p.y = initial ? rand(0, h) : def.rise ? h + 30 : -30;
      p.vy = (def.kind === "snow" ? rand(18, 55) : def.kind === "confetti" ? rand(45, 95) : rand(22, 48)) * (def.rise ? -1 : 1);
      p.vx = rand(-12, 12);
      p.sway = rand(0.4, 1.4);
      p.phase = rand(0, Math.PI * 2);
      p.rot = rand(0, Math.PI * 2);
      p.vr = rand(-1.5, 1.5);
      p.size = def.kind === "snow" ? rand(1, 3.4) : rand(5, 9);
      p.alpha = def.kind === "snow" ? rand(0.35, 0.85) : def.kind === "emoji" ? rand(0.55, 0.85) : rand(0.6, 0.95);
      p.color = palette[Math.floor(Math.random() * palette.length)];
      if (sprites) p.sprite = sprites[Math.floor(Math.random() * sprites.length)][Math.floor(Math.random() * 3)];
      return p;
    };
    const flakes = def.kind === "fireworks" ? [] : Array.from({ length: count }, () => spawn({}, true));
    const sparks = [];
    const rockets = [];
    const launch = () => {
      rockets.push({ x: rand(w * 0.15, w * 0.85), y: h + 10, vy: rand(-h * 0.95, -h * 0.75), tx: rand(h * 0.12, h * 0.45), color: palette[Math.floor(Math.random() * palette.length)] });
    };
    const burst = (x, y, color) => {
      const n = Math.round(rand(36, 60));
      for (let i = 0; i < n; i++) {
        const angle = (i / n) * Math.PI * 2 + rand(-0.05, 0.05);
        const speed = rand(60, 190);
        sparks.push({ x, y, vx: Math.cos(angle) * speed, vy: Math.sin(angle) * speed, life: 1, decay: rand(0.5, 0.85), color: Math.random() < 0.25 ? "#ffffff" : color });
      }
    };
    const frame = (t) => {
      raf = requestAnimationFrame(frame);
      const dt = Math.min(0.05, (t - last) / 1000);
      last = t;
      ctx.clearRect(0, 0, w, h);
      if (def.kind === "fireworks") {
        if (t > nextRocket) { launch(); nextRocket = t + rand(700, 1900) / intensity; }
        for (let i = rockets.length - 1; i >= 0; i--) {
          const r = rockets[i];
          r.y += r.vy * dt;
          r.vy *= 0.985;
          ctx.globalAlpha = 0.9;
          ctx.fillStyle = r.color;
          ctx.fillRect(r.x - 1, r.y, 2, 7);
          if (r.y <= r.tx || r.vy > -60) { burst(r.x, r.y, r.color); rockets.splice(i, 1); }
        }
        for (let i = sparks.length - 1; i >= 0; i--) {
          const s = sparks[i];
          s.vy += 70 * dt;
          s.vx *= 0.985; s.vy *= 0.985;
          s.x += s.vx * dt; s.y += s.vy * dt;
          s.life -= s.decay * dt;
          if (s.life <= 0) { sparks.splice(i, 1); continue; }
          ctx.globalAlpha = Math.max(0, s.life);
          ctx.fillStyle = s.color;
          ctx.beginPath();
          ctx.arc(s.x, s.y, 1.8, 0, Math.PI * 2);
          ctx.fill();
        }
        ctx.globalAlpha = 1;
        return;
      }
      flakes.forEach((p) => {
        p.phase += dt * p.sway;
        p.x += (p.vx + Math.sin(p.phase) * 18) * dt;
        p.y += p.vy * dt;
        p.rot += p.vr * dt;
        if ((def.rise && p.y < -40) || (!def.rise && p.y > h + 40) || p.x < -60 || p.x > w + 60) spawn(p, false);
        ctx.globalAlpha = p.alpha;
        if (def.kind === "snow") {
          ctx.fillStyle = "#ffffff";
          ctx.beginPath();
          ctx.arc(p.x, p.y, p.size, 0, Math.PI * 2);
          ctx.fill();
        } else if (def.kind === "confetti") {
          ctx.save();
          ctx.translate(p.x, p.y);
          ctx.rotate(p.rot);
          ctx.scale(1, Math.cos(p.phase * 2));
          ctx.fillStyle = p.color;
          ctx.fillRect(-p.size / 2, -p.size / 4, p.size, p.size / 2);
          ctx.restore();
        } else if (p.sprite) {
          ctx.save();
          ctx.translate(p.x, p.y);
          ctx.rotate(Math.sin(p.rot) * 0.5);
          ctx.drawImage(p.sprite, -p.sprite.width / 2, -p.sprite.height / 2);
          ctx.restore();
        }
      });
      ctx.globalAlpha = 1;
    };
    const onVisibility = () => {
      cancelAnimationFrame(raf);
      if (!document.hidden) { last = performance.now(); raf = requestAnimationFrame(frame); }
    };
    document.addEventListener("visibilitychange", onVisibility);
    raf = requestAnimationFrame(frame);
    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener("resize", resize);
      document.removeEventListener("visibilitychange", onVisibility);
    };
  }, [effect, colorsKey, intensity]);
  return <canvas ref={ref} aria-hidden="true" className="fixed inset-0 pointer-events-none" style={{ zIndex: 45, width: "100vw", height: "100vh" }} />;
}

// ---------- couche principale ----------

export default function SeasonalLayer({ season: saved }) {
  const location = useLocation();
  const [preview, setPreview] = useState(null);
  const [now, setNow] = useState(() => Date.now());
  const [folded, setFolded] = useState(() => store.get(FOLDED_KEY));
  const [fxOff, setFxOff] = useState(() => store.get(FX_OFF_KEY) === "1" || reducedMotion());
  const [party, setParty] = useState(null);
  const lastPhase = useRef(null);

  useEffect(() => {
    const onPreview = (e) => setPreview(e.detail || null);
    const onParty = () => setParty((p) => p || { at: Date.now(), test: true });
    window.addEventListener("vp-season-preview", onPreview);
    window.addEventListener("vp-season-celebrate", onParty);
    return () => {
      window.removeEventListener("vp-season-preview", onPreview);
      window.removeEventListener("vp-season-celebrate", onParty);
    };
  }, []);

  const cfg = preview || saved;
  // L'événement est recalculé toutes les 30 s (ou au passage à zéro) ; le compte à rebours chaque seconde.
  const slot = Math.floor(now / 30000);
  const season = useMemo(() => (cfg ? resolveSeason(cfg, slot * 30000) : null), [cfg, slot]); // eslint-disable-line react-hooks/exhaustive-deps
  const passed = season && season.phase === "countdown" && now >= season.start;
  const current = useMemo(() => (passed ? resolveSeason(cfg, now) : season), [passed, season, cfg]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!current) return undefined;
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, [current]);

  // Le compte à rebours arrive à zéro pendant la visite : feux d'artifice (sur l'accueil).
  useEffect(() => {
    if (!current) { lastPhase.current = null; return; }
    const prev = lastPhase.current;
    if (prev && prev.key === current.key && prev.phase === "countdown" && current.phase === "live") setParty({ at: Date.now() });
    lastPhase.current = { key: current.key, phase: current.phase };
  }, [current]);
  useEffect(() => {
    if (!party) return undefined;
    const id = setTimeout(() => setParty(null), 15000);
    return () => clearTimeout(id);
  }, [party]);

  if (!current) return null;
  // Panel admin et Vanta AI (application plein écran) : pas d'habillage par-dessus.
  const onApp = location.pathname.startsWith("/admin") || location.pathname === "/ai" || location.pathname.startsWith("/ai/");
  if (onApp && !preview) return null;
  const [a, b] = current.colors || ["#6366f1", "#a855f7"];
  // Sur petit écran, la carte démarre réduite (elle cacherait la page) tant qu'on ne l'ouvre pas.
  const small = typeof window !== "undefined" && window.innerWidth < 640;
  const isFolded = folded === current.key || (small && folded !== `open:${current.key}`);
  // Les feux d'artifice (Nouvel An, 14 juillet, passage à zéro) restent sur l'accueil.
  const onHome = location.pathname === "/" || Boolean(preview);
  const fireworks = SEASON_EFFECTS[current.effect]?.kind === "fireworks";
  const showFx = cfg.effects !== false && !fxOff && current.effect !== "none" && (!fireworks || onHome);
  const showParty = party && onHome;
  const showCard = cfg.banner !== false && (cfg.countdown !== false || current.message || current.phase === "live");
  const left = current.target ? splitCountdown(current.target - now) : null;

  const toggleFx = () => {
    const next = !fxOff;
    setFxOff(next);
    store.set(FX_OFF_KEY, next ? "1" : null);
  };
  const fold = (value) => {
    setFolded(value);
    store.set(FOLDED_KEY, value);
  };

  return (
    <>
      {cfg.theme !== false && (
        <>
          <div aria-hidden="true" className="fixed top-0 left-0 right-0 h-[3px] pointer-events-none" style={{ zIndex: 60, background: `linear-gradient(90deg, ${a}, ${b}, ${a})` }} />
          <div aria-hidden="true" className="fixed inset-0 pointer-events-none" style={{ zIndex: 0, background: `radial-gradient(ellipse 70% 45% at 50% -5%, ${hexToRgba(a, 0.16)}, transparent 70%), radial-gradient(ellipse 50% 40% at 100% 100%, ${hexToRgba(b, 0.08)}, transparent 70%)` }} />
        </>
      )}
      {(showFx || showParty) && <SeasonEffects effect={showParty ? "fireworks" : current.effect} colors={current.colors} intensity={showParty ? 2.2 : 1} />}
      {cfg.eggs !== false && <SeasonEasterEgg key={current.event.id} season={current} />}
      {showCard && (
        <div className="fixed left-4 bottom-4 sm:left-5 sm:bottom-5" style={{ zIndex: 48 }}>
          {preview && (
            <div className="mb-2 flex items-center gap-2">
              <span className="text-[11px] font-semibold uppercase tracking-wider bg-amber-400 text-black rounded-md px-2 py-0.5">Aperçu (non enregistré)</span>
              <button type="button" onClick={() => setPreview(null)} className="text-[11px] text-white/70 hover:text-white underline">Arrêter</button>
            </div>
          )}
          {isFolded ? (
            <button
              type="button"
              onClick={() => fold(`open:${current.key}`)}
              className="flex items-center gap-2 rounded-full pl-2.5 pr-3.5 py-1.5 text-white text-sm font-medium shadow-xl"
              style={{ background: `linear-gradient(135deg, ${hexToRgba(a, 0.9)}, ${hexToRgba(b, 0.9)})` }}
              data-testid="season-pill"
            >
              <span className="text-lg leading-none">{current.emoji}</span>
              {cfg.countdown !== false && left ? (
                <span className="font-mono tabular-nums">{left.days > 0 ? `${left.days}j ` : ""}{pad2(left.hours)}:{pad2(left.minutes)}:{pad2(left.seconds)}</span>
              ) : (
                <span>{current.title}</span>
              )}
            </button>
          ) : (
            <SeasonCard season={current} cfg={cfg} now={now} onFold={() => fold(current.key)} fxOn={!fxOff} onToggleFx={toggleFx} />
          )}
        </div>
      )}
    </>
  );
}
